import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { locales } from '../locales';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language from localStorage or default to 'en'
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  });

  const [translations, setTranslations] = useState(locales[language] || locales.en);

  useEffect(() => {
    localStorage.setItem('language', language);
    setTranslations(locales[language] || locales.en);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const t = (key) => {
    // Support nested keys like 'hero.headline'
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Return key if translation not found
        return key;
      }
    }
    
    return value;
  };

  const changeLanguage = useCallback((newLang) => {
    if (locales[newLang]) {
      setLanguage(newLang);
    }
  }, []);

  const value = useMemo(() => ({
    language, translations, t, changeLanguage
  }), [language, translations, t, changeLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
